import CountdownField from './CountdownField.jsx';

// Renders a single form field driven by a variable definition from the
// `overlays.variables` schema. Supported types: text, textarea, number,
// boolean, enum, countdown. Add a case here when extending the schema.
//
// Value reads/writes flow through string keys and the parent's onChange —
// this component doesn't know or care about the larger state shape.
//
// `serverOffset` is forwarded to interactive types that need server-time
// awareness (currently just countdown).

export default function DynamicField({
  variable,
  value,
  onChange,
  disabled = false,
  serverOffset = 0,
}) {
  if (!variable?.key) return null;
  const id = `dynfield-${variable.key.replace(/\./g, '-')}`;
  const v = value;

  const baseLabel = (
    <label className="form-label" htmlFor={id}>
      {variable.label || variable.key}
      {variable.required && <span className="text-danger ms-1">*</span>}
    </label>
  );

  const helperText = variable.description
    ? <div className="form-text">{variable.description}</div>
    : null;

  switch (variable.type) {
    case 'textarea':
      return (
        <>
          {baseLabel}
          <textarea
            id={id}
            className="form-control"
            rows={variable.rows ?? 3}
            value={v ?? ''}
            placeholder={variable.placeholder ?? ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          {helperText}
        </>
      );

    case 'number':
      return (
        <>
          {baseLabel}
          <input
            id={id}
            type="number"
            className="form-control"
            value={v ?? ''}
            placeholder={variable.placeholder ?? ''}
            disabled={disabled}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') onChange(null);
              else {
                const n = Number(raw);
                onChange(Number.isFinite(n) ? n : raw);
              }
            }}
          />
          {helperText}
        </>
      );

    case 'boolean':
      return (
        <div className="form-check pt-4">
          <input
            id={id}
            type="checkbox"
            className="form-check-input"
            checked={!!v}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          <label className="form-check-label" htmlFor={id}>
            {variable.label || variable.key}
          </label>
          {helperText}
        </div>
      );

    case 'countdown':
      return (
        <>
          {baseLabel}
          <CountdownField
            value={value ?? null}
            onChange={onChange}
            serverOffset={serverOffset}
            disabled={disabled}
          />
          {helperText}
        </>
      );

    case 'enum': {
      const options = Array.isArray(variable.options) ? variable.options : [];
      return (
        <>
          {baseLabel}
          <select
            id={id}
            className="form-select"
            value={v ?? ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          >
            {!variable.required && <option value="">— none —</option>}
            {options.map((opt) => {
              // Allow either ['BO3','BO5'] or [{value:'BO3',label:'Best of 3'}].
              const optValue = typeof opt === 'object' ? opt.value : opt;
              const optLabel = typeof opt === 'object' ? (opt.label ?? opt.value) : opt;
              return <option key={optValue} value={optValue}>{optLabel}</option>;
            })}
          </select>
          {helperText}
        </>
      );
    }

    case 'text':
    default:
      return (
        <>
          {baseLabel}
          <input
            id={id}
            type="text"
            className="form-control"
            value={v ?? ''}
            placeholder={variable.placeholder ?? ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          {helperText}
        </>
      );
  }
}
